import { useCallback, useEffect, useState } from 'react';
import type { DomainOption } from '../types';

const STORAGE_KEY = 'aiprompt:history:v1';
const MAX_ENTRIES = 100;

export interface HistoryEntry {
  id: string;
  savedAt: number;
  request: string;
  options: DomainOption[];
  selectedNumber: number | null;
}

function loadEntries(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is HistoryEntry =>
        e &&
        typeof e.id === 'string' &&
        typeof e.savedAt === 'number' &&
        typeof e.request === 'string' &&
        Array.isArray(e.options)
    );
  } catch {
    return [];
  }
}

function saveEntries(entries: HistoryEntry[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // quota or disabled storage; ignore
  }
}

function makeId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function sameSignature(
  a: { request: string; options: DomainOption[] },
  b: { request: string; options: DomainOption[] }
): boolean {
  if (a.request !== b.request) return false;
  if (a.options.length !== b.options.length) return false;
  return a.options.every((opt, i) => b.options[i]?.domain === opt.domain);
}

export function useHistory() {
  const [entries, setEntries] = useState<HistoryEntry[]>(loadEntries);

  useEffect(() => {
    saveEntries(entries);
  }, [entries]);

  const add = useCallback(
    (entry: Omit<HistoryEntry, 'id' | 'savedAt'>): string => {
      const newEntry: HistoryEntry = {
        ...entry,
        id: makeId(),
        savedAt: Date.now(),
      };
      setEntries((prev) => [newEntry, ...prev].slice(0, MAX_ENTRIES));
      return newEntry.id;
    },
    []
  );

  const updateSelection = useCallback(
    (id: string, selectedNumber: number | null) => {
      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, selectedNumber } : e))
      );
    },
    []
  );

  const remove = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const clearAll = useCallback(() => setEntries([]), []);

  const findDuplicateId = useCallback(
    (candidate: { request: string; options: DomainOption[] }): string | null => {
      const match = entries.find((e) => sameSignature(e, candidate));
      return match ? match.id : null;
    },
    [entries]
  );

  return { entries, add, updateSelection, remove, clearAll, findDuplicateId };
}
